/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FC, useEffect, useRef, useState } from "react";
import { BannerDetails, BannerPictureUpdate, BannerPictureDelete } from "../core/_requests";
import { useAuth } from "../../../auth";

type Props = {
  initialValues: any;
};

const UpdateBanner: FC<Props> = ({ initialValues }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentBanners, setCurrentBanners] = useState<string[]>([]);
  const [allowedCount, setAllowedCount] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [replaceIndex, setReplaceIndex] = useState<number | null>(null);
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [selectedDeleteIndex, setSelectedDeleteIndex] = useState<number | null>(null);

const confirmDelete = (index: number) => {
  setSelectedDeleteIndex(index);
  setShowDeleteModal(true);
};

  const chefId = currentUser?.uuid;
  // Create refs for each file input
  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const clickCancel = () => navigate("/settings");

  // Fetch current banners & allowed count
  useEffect(() => {
    if (!chefId) return;

    const fetchBannerDetails = async () => {
      try {
        const res = await BannerDetails(chefId);
        if (res?.status) {
          setCurrentBanners(res.data || []);
          setAllowedCount(res.allowedCount || 0);
          // Initialize refs array
          fileInputRefs.current = new Array(res.data?.length || 0).fill(null);
        }
      } catch (error) {
        console.error("Error fetching banner details:", error);
      }
    };

    fetchBannerDetails();
  }, [chefId]);

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, index?: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setErrorMessage("Invalid file type. Only JPEG, PNG, JPG, WEBP allowed.");
      return;
    }
    if (file.size > 1024 * 1024) {
      setErrorMessage("File size should be less than 1MB.");
      return;
    }

    // Check if adding new banner would exceed allowed count
    if (index === undefined && currentBanners.length >= allowedCount) {
      setErrorMessage(`Cannot add more than ${allowedCount} banner(s).`);
      return;
    }

    setSelectedFile(file);
    setReplaceIndex(index !== undefined ? index : null);
    setErrorMessage("");
  };

  const handleErrorMessageClose = () => {
    setErrorMessage("");
    // Clear all file inputs
    fileInputRefs.current.forEach(ref => {
      if (ref) ref.value = "";
    });
    setSelectedFile(null);
    setReplaceIndex(null);
  };

 const handleDeleteConfirmed = async () => {
  if (!chefId || selectedDeleteIndex === null) return;

  setLoading(true);
  setShowDeleteModal(false);

  try {
    const res = await BannerPictureDelete(chefId, { deleteIndex: selectedDeleteIndex });
    if (res?.status) {
      const details = await BannerDetails(chefId);
      if (details?.status) {
        setCurrentBanners(details.data || []);
        setAllowedCount(details.allowedCount || 0);
        fileInputRefs.current = new Array(details.data?.length || 0).fill(null);
      }
    } else {
      setErrorMessage(res?.message || "Failed to delete banner.");
    }
  } catch (err) {
    console.error("Error deleting banner:", err);
    setErrorMessage("Something went wrong while deleting.");
  } finally {
    setSelectedDeleteIndex(null);
    setLoading(false);
  }
};


  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({}),
    onSubmit: async () => {
      if (!selectedFile) {
        setErrorMessage("Please select a file to upload.");
        return;
      }

      const formData = new FormData();
      formData.append("bannerchef", selectedFile);

      // If replacing existing banner
      if (replaceIndex !== null) formData.append("replaceIndex", replaceIndex.toString());

      setLoading(true);
      try {
        const res = await BannerPictureUpdate(formData, chefId);
        if (res?.status) {
          setCurrentBanners(res.data.banners);
          setSelectedFile(null);
          setReplaceIndex(null);
          // Clear all file inputs
          fileInputRefs.current.forEach(ref => {
            if (ref) ref.value = "";
          });
          navigate("/settings/banner-success", { replace: true });
        } else {
          setErrorMessage(res?.message || "Failed to update banner.");
        }
      } catch (err) {
        setErrorMessage("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <section className="container-fluid">
      <div className="row">
        <section className="content-section">
          <div className="overlay"></div>
          <div className="drawer small">
            <div className="title row">
              <h3 className="col-10 mb-0">Banner Images</h3>
              <div className="col-2 text-end mb-2">
                <button className="btn p-0 close" onClick={clickCancel}>
                  <i className="material-symbols-outlined">close</i>
                </button>
              </div>
              <h6 className="mb-3">
                Hey buddy, check out this banner on your profile in user app!
              </h6>
              <p className="text-muted small">
                Currently have: {currentBanners.length} | Allowed: {allowedCount}
              </p>
            </div>

            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="col-12 mb-3 mt-3">
                {/* Upload section - only show if we haven't reached max banners */}
                {currentBanners.length < allowedCount && (
                  <div className="upload-section mb-4">
                    <h5 className="mb-2">Upload Banner Image</h5>
                    <p className="text-muted small mb-3">
                      *Supported file type (.jpg, .png, .jpeg) Max file size:1MB
                    </p>

                    <div className="upload-area border-dashed rounded p-4 text-center mb-3">
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp"
                        id="banner-upload"
                        onChange={(e) => handleFileSelect(e)}
                        style={{ display: "none" }}
                      />
                      <label htmlFor="banner-upload" className="cursor-pointer">
                        <div className="upload-placeholder">
                          <i className="material-symbols-outlined fs-1 text-muted mb-2">cloud_upload</i>
                          <p className="text-muted mb-0">Click to upload or drag and drop</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Current banners section */}
                {currentBanners.length > 0 && (
                  <div className="current-banners mb-4">
                    <h5 className="mb-3">Current Banners</h5>
                    <div className="d-flex flex-wrap gap-3">
                      {currentBanners.map((url, index) => (
                        <div
                          key={index}
                          className="d-flex flex-column align-items-center position-relative"
                        >
                          <img
                            src={url}
                            height="100"
                            width="150"
                            alt={`banner-${index}`}
                            className="mb-2 rounded object-fit-cover"
                          />
                          <input
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp"
                            ref={el => fileInputRefs.current[index] = el}
                            style={{ display: "none" }}
                            onChange={(e) => handleFileSelect(e, index)}
                          />
                          <div className="mt-2 d-flex justify-content-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => fileInputRefs.current[index]?.click()}
                              disabled={loading}
                            >
                              Replace
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => confirmDelete(index)}
                              disabled={loading}
                            >
                              <i className="material-symbols-outlined fs-6 align-middle">delete</i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected file preview */}
                {selectedFile && (
                  <div className="selected-file-preview mb-3">
                    <h6>Selected File:</h6>
                    <div className="d-flex align-items-center mt-2">
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        height="60"
                        width="100"
                        alt="selected-preview"
                        className="me-2 rounded object-fit-cover"
                      />
                      <span className="small">{selectedFile.name}</span>
                    </div>
                    <p className="small text-muted mt-1">
                      {replaceIndex !== null
                        ? `Will replace banner ${replaceIndex + 1}`
                        : "Will be added as a new banner"}
                    </p>
                  </div>
                )}

                {/* Error message */}
                {errorMessage && (
                  <div className="alert alert-danger d-flex align-items-center justify-content-between mt-3">
                    <span className="small">{errorMessage}</span>
                    <button className="btn p-0" onClick={handleErrorMessageClose}>
                      <i className="material-symbols-outlined fs-6">close</i>
                    </button>
                  </div>
                )}
              </div>

              {/* Buttons */}
              <div className="col-12 mt-4 text-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary rounded-5 me-2"
                  onClick={clickCancel}
                >
                  Cancel
                </button>
                {selectedFile && (
                  <button
                    type="submit"
                    className="btn btn-primary rounded-5"
                    disabled={loading}
                  >
                    {!loading ? "Publish" : "Uploading..."}
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>
      </div>
      {showDeleteModal && (
  <div className="modal-backdrop">
    <div className="modal-box">
      <h5 className="mb-3">Delete Banner?</h5>
      <p className="small text-muted">
        This action cannot be undone. Do you want to proceed?
      </p>

      <div className="text-end mt-4">
        <button
          className="btn btn-outline-secondary me-2"
          onClick={() => setShowDeleteModal(false)}
          disabled={loading}
        >
          Cancel
        </button>

        <button
          className="btn btn-danger"
          onClick={handleDeleteConfirmed}
          disabled={loading}
        >
          {loading ? "Deleting..." : "Yes, Delete"}
        </button>
      </div>
    </div>
  </div>
)}


      <style>{`
        .border-dashed {
          border: 2px dashed #dee2e6;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .object-fit-cover {
          object-fit: cover;
        }
        .upload-area:hover {
          background-color: #f8f9fa;
          border-color: #0d6efd;
          
        }
          .modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.modal-box {
  background: #fff;
  padding: 24px;
  border-radius: 10px;
  width: 350px;
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

      `}</style>
    </section>
  );
};

export { UpdateBanner };